import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheEntry {
  response: HttpResponse<any>;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class CacheInterceptor implements HttpInterceptor {
  private static cache = new Map<string, CacheEntry>();
  
  private readonly CACHE_DURATION = {
    catalog: 7 * 24 * 60 * 60 * 1000, // 7 días
    establishments: 3 * 24 * 60 * 60 * 1000, // 3 días
    exhibitions: 2 * 24 * 60 * 60 * 1000, // 2 días
    default: 60 * 60 * 1000 // 1 hora
  };

  private readonly CACHE_FIRST_PATTERNS = [
    /\/brands$/,
    /\/categories$/,
    /\/event-types$/,
    /\/establishments\/types$/,
    /\/users\/profile$/,
    /\/establishments$/,
    /\/establishments\/\d+$/,
    /\/exhibitions$/,
    /\/exhibitions\/\d+$/
  ];

  private readonly NO_CACHE_PATTERNS = [
    /\/auth\//,
    /\/evaluations\/send$/,
    /\/files\/upload$/
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Solo cachear GET requests
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    // No cachear endpoints específicos
    if (this.shouldNotCache(req.url)) {
      return next.handle(req);
    }

    // Verificar si debe usar caché
    if (!this.shouldCache(req.url)) {
      return next.handle(req);
    }

    // Intentar obtener de caché
    const cachedResponse = this.getFromCache(req.url);
    if (cachedResponse) {
      console.log('📦 Cache HIT:', req.url);
      return of(cachedResponse.response.clone());
    }

    // Si no está en caché, hacer request y guardar
    console.log('🌐 Cache MISS:', req.url);
    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.addToCache(req.url, event);
        }
      })
    );
  }

  private shouldCache(url: string): boolean {
    return this.CACHE_FIRST_PATTERNS.some(pattern => pattern.test(url));
  }

  private shouldNotCache(url: string): boolean {
    return this.NO_CACHE_PATTERNS.some(pattern => pattern.test(url));
  }

  private getFromCache(url: string): CacheEntry | null {
    const entry = CacheInterceptor.cache.get(url);
    if (!entry) {
      return null;
    }

    const duration = this.getCacheDuration(url);
    const isExpired = Date.now() - entry.timestamp > duration;

    if (isExpired) {
      CacheInterceptor.cache.delete(url);
      console.log('🗑️ Cache EXPIRED:', url);
      return null;
    }

    return entry;
  }

  private addToCache(url: string, response: HttpResponse<any>): void {
    const entry: CacheEntry = {
      response: response.clone(),
      timestamp: Date.now()
    };
    CacheInterceptor.cache.set(url, entry);
    console.log('💾 Cache SAVED:', url, `(${CacheInterceptor.cache.size} entries)`);
  }

  private getCacheDuration(url: string): number {
    if (/\/(brands|categories|event-types|establishments\/types|users\/profile)$/.test(url)) {
      return this.CACHE_DURATION.catalog;
    }
    if (/\/establishments/.test(url)) {
      return this.CACHE_DURATION.establishments;
    }
    if (/\/exhibitions/.test(url)) {
      return this.CACHE_DURATION.exhibitions;
    }
    return this.CACHE_DURATION.default;
  }

  clearCache(): void {
    CacheInterceptor.cache.clear();
    console.log('🗑️ Cache CLEARED');
  }

  getCacheSize(): number {
    return CacheInterceptor.cache.size;
  }

  getCacheEntries(): string[] {
    return Array.from(CacheInterceptor.cache.keys());
  }
}
